
import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';

import './MitekDesktopUpdater.css';
import { checkForPhoneNumber, getApplicantId, replaceWithCustomerNumber } from '../../providers/MitekProvider/MitekUtils';

import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { acquisitionApiRequest } from '../../utils/acquisition-api/AcquisitionApi';
import SessionStorage from '../../utils/SessionStorage';
import { InitializeAcquisitionApiResponse } from '../../types/responses/InitializeAcquisitionApi';
import { useWidthContext } from '../../providers/Width';
import { clearSessionAndRedirectToDataEntryPage } from '../../utils/acquisition-utils';
import { Row, Col, Title, Image, Container } from '../../importCL';
import { callResendSMS, RefreshCurrentApplication } from './MitekDesktopUpdaterCalls';
import { callResendSMS as callResendSMSModern, RefreshCurrentApplication as RefreshCurrentApplicationModern } from './MitekDesktopUpdaterCallsModern';


export interface IMitekDesktopUpdaterProps extends MappedComponentProperties {
    usingModernApi?: boolean,
    imgPath?: string, 
    imgAltText?: string, 
    mainTitle?: string, 

    instructions?: string, 
    resendInstructions?: string, 
    steps?: {value: string}[],
    smsAnchorTagText?: string, 
    retrieveDocsTimeoutDuration?: string, 

    dataEntryUrl?: string, 
    fullApplicationPage?: string, 
    counterSingleOfferUrlPQ?: string, 
    limitedEsignUrl?: string, 
    riskBasedEsignUrl?: string, 
    confirmationPageUrl?: string, 
    pendingApprovalPageUrl?: string, 
    levelThreePageUrl?: string, 
    mobileWalletUrl?: string, 
    oneTimePasscodeUrl?: string, 
    pendingFundingUrl?: string,

    generalErrorCode?: string, 
    apiErrorCode?: string, 
    lostErrorCode?: string, 
    zipErrorCode?: string, 
    resendSMSErrorCode?: string, 

    usingConfirmBackNavigation?: boolean,
    usingAutoSendingSms?: boolean,
    confirmBackNavigationPrompt?: string,
}

export const MitekDesktopUpdaterEditConfig = {
    emptyLabel: 'Mitek Desktop Updater',
    isEmpty: (props: PropsWithChildren<IMitekDesktopUpdaterProps>) => {
        return !props || !props?.mainTitle;
    },
};

const MitekDesktopUpdater = (props: PropsWithChildren<IMitekDesktopUpdaterProps>): JSX.Element => {
    const { showPushdown } = usePushdownContext();
    const [ applicantPhoneNumber, setApplicantPhoneNumber ] = useState();

    /**
     * TODO: Resolve how to handle each Error Case in External Mitek Flow
     * 
     * Currently redirects the user to their respective Data Entry Page,
     * However behaviour isn't concrete yet.
     * 
     * @param reason - Printed to the Console.error
     */
    const errorPushdownAPI = (reason: any) => {
        console.error(reason);
        clearSessionAndRedirectToDataEntryPage(props.generalErrorCode);
    };

    const sessionIDRef = useRef('');
    const sendSmsRef = useRef(false);
    const isApplicationRefreshing = useRef(false);
    const intervalRef = useRef<NodeJS.Timeout>(null);

    const { width } = useWidthContext();
    const isMobile = width <= 768;

    // ---- Feature Flagged Behaviour START, once whichever path is decided, delete the other.

    const callRefreshCurrentApplication = useCallback( props.usingModernApi ? 
        RefreshCurrentApplicationModern(props, errorPushdownAPI, intervalRef) :
        RefreshCurrentApplication(props, errorPushdownAPI, intervalRef), 
        [RefreshCurrentApplication, props, errorPushdownAPI, intervalRef],
    );

    // ---- Feature Flaggs Behaviour END

    const resendSMS = useCallback(props.usingModernApi ? 
        callResendSMSModern(props, showPushdown) : 
        callResendSMS(props, showPushdown), 
        [callResendSMS, props, showPushdown]);

    const backNavigationConfirmation: EventListener = useCallback(() => {
        if (window.confirm(props?.confirmBackNavigationPrompt ? props.confirmBackNavigationPrompt : undefined)) {
            window.history.back();
        } else {
            window.history.pushState({}, '', window.location.href);
        }
    }, []);

    useEffect(() => {

        const initSession = async () => {
            try {
                if (!props.usingModernApi && !SessionStorage.verifyNotEmpty('initResponse')) { 
                    const initResponse = await acquisitionApiRequest<InitializeAcquisitionApiResponse>('init-acquisition-api', {});
                    if (initResponse.status !== 200) {
                        throw initResponse;
                    }
                    SessionStorage.setObject('initResponse', initResponse.data);
                }
            
                sessionIDRef.current = await getApplicantId();

                if (props.usingAutoSendingSms && resendSMS && !sendSmsRef.current) {
                    // First instance of sending SMS
                    try {
                        sendSmsRef.current = true;
                        if (!await resendSMS()) {
                            throw new Error('Send SMS Failed');
                        }
                    } catch (smsFailure) {
                        sendSmsRef.current = false;
                    }
                }
                if (!props.isInEditor && props.retrieveDocsTimeoutDuration && props.retrieveDocsTimeoutDuration !== '0') {
                    intervalRef.current = setInterval(() => {
                        if (isApplicationRefreshing.current) {
                            return;
                        }
                        isApplicationRefreshing.current = true;
                        callRefreshCurrentApplication().finally(() => {  
                            isApplicationRefreshing.current = false
                        })
                    }, parseInt(props.retrieveDocsTimeoutDuration));
                }
            } catch (err) {
                errorPushdownAPI(err);
            }
        };

        initSession();

        const retrievePhone = async () => {
            setApplicantPhoneNumber(await checkForPhoneNumber());
        };

        retrievePhone();

        if (props.usingConfirmBackNavigation) {
            window.addEventListener('popstate', backNavigationConfirmation, true);
            window.history.pushState({}, '', window.location.href);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            if (props.usingConfirmBackNavigation) {
                window.removeEventListener('popstate', backNavigationConfirmation, true);
            }
        };
    }, [callRefreshCurrentApplication, resendSMS]);

    return (
        <Container id='mitek-desktop-updater' className='MitekDesktopUpdater__container'>
            <Row>
                <Col>
                    {props.imgPath &&
                        <Image
                            loading='lazy'
                            className='MitekDesktopUpdater__mainImage'
                            src={props.imgPath}
                            alt={props.imgAltText} />
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    {props.mainTitle &&
                        <Title className={`MitekDesktopUpdater__title${isMobile ? 'Mobile' : ''}`} tag='h1'>{transformHTMLParserLinks(props.mainTitle)}</Title>
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    {props.instructions &&
                        <div className='MitekDesktopUpdater__instructions'>{transformHTMLParserLinks(replaceWithCustomerNumber(applicantPhoneNumber, props.instructions))}</div>
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    {props.resendInstructions &&
                        <div className='MitekDesktopUpdater__resendInstructions'>{transformHTMLParserLinks(replaceWithCustomerNumber(applicantPhoneNumber, props.resendInstructions))}
                            <span 
                                className='MitekDesktopUpdater__resendInstructions--link'
                                onClick={resendSMS}>
                                {props.smsAnchorTagText ? transformHTMLParserLinks((replaceWithCustomerNumber(applicantPhoneNumber, props.smsAnchorTagText))) : 'Resend Text Message'}
                            </span>
                        </div>
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    <ul className={`MitekDesktopUpdater__bulletList${isMobile ? 'Mobile' : ''}`}>
                        {props.steps && props.steps.map((element, index) => {
                            return (
                                <li className='MitekDesktopUpdater__bullet' key={index}>
                                    <svg width='31px' height='31px' viewBox='0 0 31 31' version='1.1' xmlns='http://www.w3.org/2000/svg'>
                                        <g id='Mitek-+-OTP----Design' stroke='none' strokeWidth='1' fill='none' fillRule='evenodd'>
                                            <g id='Desktop-Identity-Loading' transform='translate(-632.000000, -660.000000)'>
                                                <g id='Group-6' transform='translate(632.000000, 504.000000)'>
                                                    <g id='Group-2' transform='translate(0.000000, 156.000000)'>
                                                        <g id='Green-Checkmark-Copy-4'>
                                                            <circle className='MitekDesktopUpdater__circle' id='Oval' fill='#EEEEEE' cx='15.5' cy='15.5' r='15.5'/>
                                                            <polyline className='MitekDesktopUpdater__line' id='Path-3' stroke='#BEBEBE' strokeWidth='4' strokeLinecap='round' strokeLinejoin='round' points='7 15.9348947 13.8528692 24 22 9'/>
                                                        </g>
                                                    </g>
                                                </g>
                                            </g>
                                        </g>
                                    </svg>
                                    <div className={`Bullet Bullet-${index}`}>{transformHTMLParserLinks(element.value)}</div>
                                </li>
                            );
                        })}
                    </ul>
                </Col>
            </Row>
        </Container>);
};

export default MitekDesktopUpdater;
